import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { M } from "./tokens";
import { ADS } from "./assets";
import { MACHINES } from "./machines";
import { FullScreenEmbed, useModalChrome } from "./FullScreenEmbed";
import { usePrefersReducedMotion } from "../../lib/useReveal";

const META_FRAMES =
  MACHINES.find((m) => m.id === "meta")?.steps.find((s) => s.kind === "frames")?.data.frames || [];

/* The living week. One flagship action per day, shown with the real
   asset behind it. Days tick forward, any day is clickable. */

const TAGS = {
  meta: ["#4C9AFF", "meta"],
  google: ["#FFC46B", "google"],
  klaviyo: ["#A78BFA", "klaviyo"],
  cro: ["#5FE3A1", "cro"],
  creative: ["#FF8FA3", "creative"],
  report: ["#7DD3FC", "report"],
};

const DAYS = [
  {
    d: "MON", t: "06:40", m: "meta",
    h: "50 new ads launched for the quiz offer on Meta",
    s: "Hooks pulled from your five star reviews. Eight of the fifty:",
    more: "+4 smaller actions, all logged",
    visual: { type: "ads" },
  },
  {
    d: "TUE", t: "09:00", m: "cro",
    h: "New quiz test live on the question with the highest drop off",
    s: "Question two loses 18% of takers. Two wordings now splitting traffic.",
    more: "+3 smaller actions, all logged",
    visual: { type: "quizsplit", route: "/axos/quiz", native: 1280, label: "quiz.ibdassist.com" },
  },
  {
    d: "WED", t: "06:15", m: "klaviyo",
    h: "Winback email winner promoted: 48.9% opens against 41.2%",
    s: "The loser is gone. A new version built to beat the winner is already sending.",
    more: "+4 smaller actions, all logged",
    visual: { type: "page", route: "/axos/emails", native: 1000, label: "klaviyo · winback flow", blurb: "The actual lifecycle emails, rendered at full size. Open them full screen." },
  },
  {
    d: "THU", t: "07:30", m: "cro",
    h: "New landing page test: hero image, built on last week's headline winner",
    s: "The winning headline stays. Now the image under it earns its spot.",
    more: "+3 smaller actions, all logged",
    visual: { type: "page", route: "/axos", native: 1280, label: "ibdassist.com", blurb: "The live landing page, block by block. Open it full screen." },
  },
  {
    d: "FRI", t: "05:30", m: "google",
    h: "Google budget moved to the search terms holding ROAS above 3",
    s: "Raised where people buy, blocked where they never do.",
    more: "+5 smaller actions, all logged",
    visual: {
      type: "table",
      rows: [
        ["best supplement for hashimotos", "ROAS 3.4", "budget raised", "ok"],
        ["gut and thyroid multivitamin", "ROAS 3.1", "budget raised", "ok"],
        ["cheap thyroid vitamins", "0 orders", "blocked", "danger"],
        ["why am i always bloated", "0 orders", "blocked", "danger"],
      ],
    },
  },
  {
    d: "SAT", t: "08:00", m: "creative",
    h: "New advertorial live to convert the buyers who need convincing",
    s: "Same offer, told as a story, for the clicks a product page loses.",
    more: "+2 smaller actions, all logged",
    visual: { type: "page", route: "/axos/advertorial", native: 1280, label: "the daily wellness read", blurb: "The full advertorial, exactly as the click sees it. Open it full screen." },
  },
  {
    d: "SUN", t: "18:00", m: "report",
    h: "One page memo sent: what made money, what is next",
    s: "Two decisions need you Monday. Everything else is already done.",
    more: "every action has a receipt and an undo",
    visual: { type: "page", route: "/axos/report", native: 920, label: "the monday one pager", blurb: "The real memo, exactly as it lands Monday 06:00. Open it full screen." },
  },
];

const V_H = 250;

function AdLightbox({ i, setI, onClose }) {
  useModalChrome(onClose);
  const f = META_FRAMES[i] || {};
  const prev = () => setI((i + ADS.length - 1) % ADS.length);
  const next = () => setI((i + 1) % ADS.length);
  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4"
      style={{ background: "rgba(4,5,7,0.88)", backdropFilter: "blur(6px)" }}
      data-testid="ad-lightbox"
      onClick={onClose}
    >
      <button
        onClick={(e) => { e.stopPropagation(); prev(); }}
        data-testid="ad-lightbox-prev"
        aria-label="Previous ad"
        className="focus-ring mr-4 hidden shrink-0 rounded-full border p-2.5 sm:block"
        style={{ borderColor: M.border, color: M.ink, background: "rgba(7,8,10,0.7)" }}
      >
        <ChevronLeft size={16} />
      </button>
      <div
        className="flex max-h-[92vh] w-[min(420px,88vw)] flex-col overflow-hidden rounded-2xl border"
        style={{ borderColor: M.border, background: M.surface }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b px-4 py-2.5" style={{ borderColor: M.border, background: M.surface2 }}>
          <span className="f-mono text-[9px] uppercase tracking-[0.16em]" style={{ color: M.muted }}>
            Batch #34 · ad {String(i + 1).padStart(2, "0")} / 08
          </span>
          <button
            onClick={onClose}
            data-testid="ad-lightbox-close"
            aria-label="Close ad"
            className="focus-ring ml-auto rounded p-1.5"
            style={{ border: `1px solid ${M.border}`, color: M.ink }}
          >
            <X size={13} />
          </button>
        </div>
        <img src={ADS[i]} alt={f.angle || `Axos ad ${i + 1}`} className="w-full object-cover" style={{ aspectRatio: "3 / 4" }} data-testid="ad-lightbox-img" />
        <div className="flex items-center gap-3 border-t px-4 py-3" style={{ borderColor: M.border, background: M.surface2 }}>
          <span className="min-w-0 flex-1 truncate text-[12.5px]" style={{ color: M.ink }}>{f.hook}</span>
          <span className="f-mono shrink-0 rounded px-2 py-1 text-[8px] uppercase tracking-[0.1em]" style={{ color: "#4C9AFF", border: "1px solid #4C9AFF44", background: "#4C9AFF0e" }}>
            {f.angle || "static"}
          </span>
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); next(); }}
        data-testid="ad-lightbox-next"
        aria-label="Next ad"
        className="focus-ring ml-4 hidden shrink-0 rounded-full border p-2.5 sm:block"
        style={{ borderColor: M.border, color: M.ink, background: "rgba(7,8,10,0.7)" }}
      >
        <ChevronRight size={16} />
      </button>
    </div>,
    document.body
  );
}

function AdsVisual() {
  const [open, setOpen] = useState(null);
  return (
    <>
      <div className="grid h-full grid-cols-4 grid-rows-2 gap-2" data-testid="ops-visual-ads">
        {ADS.map((src, i) => (
          <button
            key={src}
            onClick={() => setOpen(i)}
            data-testid={`ops-ad-${i}`}
            aria-label={`Open ad ${i + 1} full size`}
            className="focus-ring overflow-hidden rounded-md border transition-transform hover:-translate-y-[2px]"
            style={{ borderColor: M.border, minHeight: 0 }}
          >
            <img
              src={src}
              alt={`Axos ad ${i + 1}`}
              className="h-full w-full object-cover"
              style={{ animation: `sixIn 420ms cubic-bezier(0.16,1,0.3,1) ${i * 55}ms both` }}
            />
          </button>
        ))}
      </div>
      {open !== null && <AdLightbox i={open} setI={setOpen} onClose={() => setOpen(null)} />}
    </>
  );
}

function QuizSplitVisual({ v }) {
  const [full, setFull] = useState(false);
  const CARDS = [
    { k: "A", tag: "control", q: "What are you trying to improve first?", opts: ["Energy through the afternoon", "Brain fog and focus", "Digestion and bloating"] },
    { k: "B", tag: "challenger", q: "What is your biggest struggle right now?", opts: ["Afternoon crashes", "Foggy mornings", "Bloating after meals"] },
  ];
  return (
    <div className="flex h-full flex-col" data-testid="ops-visual-quizsplit">
      <div className="flex flex-wrap items-center gap-2">
        <span className="f-mono rounded px-2 py-1 text-[8px] uppercase tracking-[0.12em]" style={{ color: M.danger, border: `1px solid ${M.danger}44`, background: `${M.danger}0e` }}>
          Question 2 · 18% quit here
        </span>
        <span className="f-mono text-[8px] uppercase tracking-[0.12em]" style={{ color: M.dim }}>traffic split 50 / 50</span>
      </div>
      <div className="mt-2 grid min-h-0 flex-1 grid-cols-2 gap-2">
        {CARDS.map((c, ci) => (
          <div
            key={c.k}
            className="flex min-h-0 flex-col overflow-hidden rounded-lg border p-2.5"
            style={{ borderColor: c.k === "B" ? `${M.ok}66` : M.border, background: M.surface2, animation: `sixIn 420ms cubic-bezier(0.16,1,0.3,1) ${ci * 90}ms both` }}
          >
            <div className="flex items-center gap-1.5">
              <span className="f-mono text-[9.5px] font-bold" style={{ color: c.k === "B" ? M.ok : M.muted }}>{c.k}</span>
              <span className="f-mono text-[7.5px] uppercase tracking-[0.12em]" style={{ color: c.k === "B" ? M.ok : M.dim }}>{c.tag}</span>
            </div>
            <div className="mt-1.5 text-[11.5px] font-semibold leading-snug" style={{ color: M.ink }}>{c.q}</div>
            <div className="mt-2 space-y-1.5">
              {c.opts.map((o) => (
                <div key={o} className="truncate rounded border px-2 py-1.5 text-[9.5px] leading-none" style={{ borderColor: M.border, color: M.muted }}>
                  {o}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2.5 flex gap-2">
        <button
          onClick={() => setFull(true)}
          data-testid="ops-fullscreen-open"
          className="focus-ring f-mono rounded px-3.5 py-2 text-[9px] font-semibold uppercase tracking-[0.12em]"
          style={{ background: M.ok, color: "#04170F" }}
        >
          Full screen
        </button>
        <Link
          to={`${v.route}?tests=1`}
          data-testid="ops-visual-open"
          className="focus-ring f-mono rounded px-3.5 py-2 text-[9px] uppercase tracking-[0.12em]"
          style={{ border: `1px solid ${M.border}`, color: M.ink }}
        >
          Walk it
        </Link>
      </div>
      {full && <FullScreenEmbed route={v.route} label={v.label} onClose={() => setFull(false)} />}
    </div>
  );
}

function PageVisual({ v }) {
  const [full, setFull] = useState(false);
  const thumbW = 200;
  const scale = thumbW / v.native;
  return (
    <div className="flex h-full gap-4" data-testid="ops-visual-page">
      <button
        onClick={() => setFull(true)}
        data-testid="ops-visual-thumb"
        aria-label={`Open ${v.label} full screen`}
        className="focus-ring relative h-full shrink-0 overflow-hidden rounded-lg border transition-transform hover:-translate-y-[2px]"
        style={{ width: thumbW, borderColor: M.border, background: "#FDFBF7" }}
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <iframe
            title={v.label}
            src={`${v.route}?embed=1`}
            loading="lazy"
            scrolling="no"
            tabIndex={-1}
            style={{
              width: v.native,
              height: Math.ceil(V_H / scale),
              border: 0,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              pointerEvents: "none",
            }}
          />
        </div>
      </button>
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <span className="f-mono text-[8.5px] uppercase tracking-[0.14em]" style={{ color: M.dim }}>{v.label}</span>
        <p className="mt-2 max-w-[32ch] text-[12.5px] leading-[1.6]" style={{ color: M.muted }}>{v.blurb}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => setFull(true)}
            data-testid="ops-fullscreen-open"
            className="focus-ring f-mono rounded px-3.5 py-2 text-[9px] font-semibold uppercase tracking-[0.12em]"
            style={{ background: M.ok, color: "#04170F" }}
          >
            Full screen
          </button>
          <Link
            to={`${v.route}?tests=1`}
            data-testid="ops-visual-open"
            className="focus-ring f-mono rounded px-3.5 py-2 text-[9px] uppercase tracking-[0.12em]"
            style={{ border: `1px solid ${M.border}`, color: M.ink }}
          >
            Walk it
          </Link>
        </div>
      </div>
      {full && <FullScreenEmbed route={v.route} label={v.label} onClose={() => setFull(false)} />}
    </div>
  );
}

function TableVisual({ v }) {
  return (
    <div className="flex h-full flex-col justify-center rounded-lg border px-4" style={{ borderColor: M.border, background: M.surface2 }} data-testid="ops-visual-table">
      {v.rows.map(([term, num, verdict, s], i) => (
        <div
          key={term}
          className="flex items-center gap-3 border-b py-2.5 last:border-b-0"
          style={{ borderColor: M.border, animation: `sixIn 420ms cubic-bezier(0.16,1,0.3,1) ${i * 80}ms both` }}
        >
          <span className="min-w-0 flex-1 truncate text-[12px]" style={{ color: M.ink }}>"{term}"</span>
          <span className="f-mono tnum shrink-0 text-[10px]" style={{ color: M.muted }}>{num}</span>
          <span
            className="f-mono shrink-0 rounded px-2 py-[3px] text-[8px] uppercase tracking-[0.1em]"
            style={{
              color: s === "ok" ? M.ok : M.danger,
              border: `1px solid ${s === "ok" ? M.ok : M.danger}44`,
              background: s === "ok" ? `${M.ok}0e` : `${M.danger}0e`,
            }}
          >
            {verdict}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function OpsReport({ embedded = false }) {
  const reduced = usePrefersReducedMotion();
  const [day, setDay] = useState(0);
  const [auto, setAuto] = useState(true);
  const [armed, setArmed] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setArmed(true); return; }
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => { if (e.isIntersecting) setArmed(true); }),
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!armed || !auto) return;
    const id = setTimeout(() => setDay((d) => (d + 1) % 7), reduced ? 3200 : 4600);
    return () => clearTimeout(id);
  }, [armed, auto, day, reduced]);

  const pick = (i) => { setAuto(false); setDay(i); };
  const x = DAYS[day];
  const [tagC, tagT] = TAGS[x.m];

  return (
    <div
      ref={ref}
      data-testid="ops-report"
      className={embedded ? "" : "overflow-hidden rounded-2xl border"}
      style={embedded ? {} : { borderColor: M.border, background: M.surface, boxShadow: "0 30px 80px rgba(0,0,0,0.55)" }}
    >
      <div className="flex items-center gap-2.5 border-b px-4 py-3" style={{ borderColor: M.border, background: M.surface2 }}>
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full" style={{ background: M.ok, opacity: 0.5, animation: reduced ? "none" : "led 1.6s ease-in-out infinite" }} />
          <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: M.ok }} />
        </span>
        <span className="f-mono text-[9px] uppercase tracking-[0.18em]" style={{ color: M.muted }}>
          Axos · operations · this week
        </span>
        <button
          onClick={() => setAuto((a) => !a)}
          data-testid="ops-play-toggle"
          className="focus-ring f-mono ml-auto rounded px-2 py-1 text-[8.5px] uppercase tracking-[0.14em]"
          style={{ border: `1px solid ${M.border}`, color: auto ? M.ok : M.dim }}
        >
          {auto ? "live" : "paused"}
        </button>
      </div>

      <div className="flex border-b" style={{ borderColor: M.border }}>
        {DAYS.map((w, i) => (
          <button
            key={w.d}
            onClick={() => pick(i)}
            data-testid={`ops-day-${i}`}
            aria-selected={i === day}
            className="focus-ring f-mono flex-1 border-r px-1 py-2 text-[8.5px] tracking-[0.16em] transition-colors last:border-r-0"
            style={{
              borderColor: M.border,
              color: i === day ? M.ok : M.dim,
              background: i === day ? "rgba(95,227,161,0.08)" : "transparent",
            }}
          >
            {w.d}
          </button>
        ))}
      </div>

      <div className="px-4 pb-4 pt-3.5">
        <div key={day} style={{ animation: reduced ? "none" : "sixIn 380ms cubic-bezier(0.16,1,0.3,1) both" }}>
          <div className="flex items-center gap-2.5">
            <span className="f-mono tnum text-[9.5px]" style={{ color: M.dim }}>{x.t}</span>
            <span
              className="f-mono rounded px-1.5 py-[2px] text-[7.5px] uppercase tracking-[0.1em]"
              style={{ color: tagC, border: `1px solid ${tagC}44`, background: `${tagC}0e` }}
            >
              {tagT}
            </span>
            <span className="f-mono ml-auto text-[8px] uppercase tracking-[0.1em]" style={{ color: M.dim }}>{x.more}</span>
          </div>
          <h3 className="f-grotesk mt-2.5 text-[17px] font-bold leading-[1.2] sm:text-[19px]" data-testid="ops-headline">
            {x.h}
          </h3>
          <p className="mt-1.5 text-[12px] leading-snug" style={{ color: M.muted }}>{x.s}</p>
          <div className="mt-3" style={{ height: V_H }} data-testid="ops-visual">
            {x.visual.type === "ads" && <AdsVisual />}
            {x.visual.type === "quizsplit" && <QuizSplitVisual v={x.visual} />}
            {x.visual.type === "page" && <PageVisual v={x.visual} />}
            {x.visual.type === "table" && <TableVisual v={x.visual} />}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t px-4 py-2.5" style={{ borderColor: M.border, background: M.surface2 }}>
        <span className="f-mono text-[8.5px] uppercase tracking-[0.12em]" style={{ color: M.ok }}>47 actions this week</span>
        <span className="f-mono text-[8.5px] uppercase tracking-[0.12em]" style={{ color: M.warn }}>3 waited on a person</span>
        <span className="f-mono ml-auto text-[8.5px] uppercase tracking-[0.12em]" style={{ color: M.dim }}>every one reversible</span>
      </div>
    </div>
  );
}
